import { useState, useEffect, useRef, useCallback } from 'react';

const HEADER_HEIGHT = 60;

/**
 * Core scroll-based focus system
 * Determines which event card is currently in focus based on scroll position
 * Uses requestAnimationFrame for 60fps performance
 */
export function useScrollFocus(totalItems: number): {
  focusedIndex: number;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  scrollToIndex: (index: number) => void;
  isReady: boolean;
} {
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const [isReady, setIsReady] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const itemPositionsRef = useRef<Map<number, number>>(new Map());

  // Update item positions on scroll and resize
  const updateFocusedIndex = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || totalItems === 0) return;

    const scrollTop = container.scrollTop;
    const focusLine = scrollTop + HEADER_HEIGHT;

    let closestIndex = 0;
    let closestDistance = Infinity;

    // Check each item's position
    for (let i = 0; i < totalItems; i++) {
      const itemTop = itemPositionsRef.current.get(i) ?? 0;
      const distance = Math.abs(itemTop - focusLine);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    setFocusedIndex(closestIndex);
  }, [totalItems]);

  // Scroll to a specific index
  const scrollToIndex = useCallback((index: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const itemTop = itemPositionsRef.current.get(index);
    if (itemTop !== undefined) {
      container.scrollTo({
        top: itemTop - HEADER_HEIGHT,
        behavior: 'smooth'
      });
    }
  }, []);

  // Throttled scroll handler using requestAnimationFrame
  const scrollHandlerRef = useRef<boolean>(false);

  const handleScroll = useCallback(() => {
    if (!scrollHandlerRef.current) {
      scrollHandlerRef.current = true;
      requestAnimationFrame(() => {
        updateFocusedIndex();
        scrollHandlerRef.current = false;
      });
    }
  }, [updateFocusedIndex]);

  // Update item positions when items change
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updatePositions = () => {
      const children = container.children;
      itemPositionsRef.current.clear();

      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        itemPositionsRef.current.set(i, child.offsetTop);
      }

      updateFocusedIndex();
      setIsReady(true);
    };

    // Use ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(updatePositions);

    // Observe all children
    Array.from(container.children).forEach((child) => {
      resizeObserver.observe(child);
    });

    // Initial update
    updatePositions();

    return () => {
      resizeObserver.disconnect();
    };
  }, [totalItems, updateFocusedIndex]);

  // Set up scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return { focusedIndex, scrollContainerRef, scrollToIndex, isReady };
}
